import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { z } from 'zod';

import type { editChargeableItemRouteStateSchema } from './routeStateSchemas';

export interface ChargeableItemContextValue {
  currentURLHashWithBookId: string;
  breadcrumbs: Array<{ pathname: string; breadcrumbTitle: string }>;
  moreLinks: Array<{ label: string; url: string }>;
  navigateToEditChargeableItem: (args: {
    chargeableItemId: string;
    editChargeableItemRouteState?: z.infer<
      typeof editChargeableItemRouteStateSchema
    >;
  }) => void;
  buildChargeableItemHref: (chargeableItemId: string) => string;
  navigateToCreateChargeableItem: () => void;
  navigateToChargeableItemsList: () => void;
  navigateToCreateStockAdjustment: (args: { chargeableItemId: string }) => void;
  navigateToCloseOrCancelChargeableItem: () => void;
}

const ChargeableItemContext = createContext<ChargeableItemContextValue | null>(
  null
);

export function useChargeableItemContext(): ChargeableItemContextValue {
  const context = useContext(ChargeableItemContext);

  if (!context) {
    throw new Error(
      'useChargeableItemContext must be used within a ChargeableItemProvider'
    );
  }

  return context;
}

export function ChargeableItemProvider(props: {
  value: ChargeableItemContextValue;
  children: ReactNode;
}) {
  return (
    <ChargeableItemContext.Provider value={props.value}>
      {props.children}
    </ChargeableItemContext.Provider>
  );
}
